import { useNavigate } from '@tanstack/react-router'
import { useState } from 'react'
import emailIcon from '../../../assets/email.svg'
import AuthRightPanel from '../../../components/auth/AuthRightPanel'
import OnboardingCard from '../../../components/common/OnboardingCard'
import ErrorPopup from '../../../components/common/errorPopup'
import { supabase } from '../../../hooks/supabase'

export default function ResetPassword() {
  const [email, setEmail] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [isErrorPopupOpen, setIsErrorPopupOpen] = useState(false)
  const [isSuccess, setIsSuccess] = useState(false)
  const navigate = useNavigate()

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    setIsSuccess(false)
    
    try {
      const { error: resetError, data } = await supabase
        .auth
        .resetPasswordForEmail(email, {
          redirectTo: `${window.location.origin}/create-new-password`
        })

      if (resetError) throw resetError
 
      setIsSuccess(true)
      
      navigate({ 
        to: '/signin',
        search: {
          message: 'Password reset email sent. Please check your inbox.'
        }
      })
    } catch (error) {
      console.error('Reset password error:', error)
      setError(error instanceof Error ? error.message : 'Failed to send reset password email')
      setIsErrorPopupOpen(true)
    }
  }

  const leftPanelContent = (
    <div className="flex flex-col h-full">
      <div className="flex flex-col p-[16px]">
        <div className="mb-1">
          <div className="text-[32px] font-semibold text-left">Forgot your password?</div>
          <p className="text-sm font-light text-[#9EA0A0] mb-8 text-left">No worries, we'll send you reset instructions</p>
          
          <form onSubmit={handleResetPassword} className="flex flex-col">
            <div className="space-y-3">
              <div className="relative">
                <img 
                  src={emailIcon} 
                  alt="Email" 
                  className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#5C5E63]"
                />
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Enter your work email"
                  className="w-full h-[40px] pl-10 pr-4 text-left rounded-[10px] text-[#222222] text-sm placeholder:text-[#9FA1A7] focus:outline-none focus:ring-1 focus:ring-[#29DC5B] focus:border-transparent shadow-[0px_0px_0px_1px_#EEEFF1_inset]"
                  required
                />
              </div>
            </div>

            <button
              type="submit"
              className="w-full h-[40px] bg-[#29DC5B] mt-[28px] text-[#222222] rounded-[10px] flex items-center justify-center text-sm font-normal hover:bg-[#22c55e] transition-colors shadow-[0px_0px_0px_1px_#2325291A_inset]"
            >
              Reset password
            </button>
          </form>

          {isSuccess && (
            <div className="mt-4 p-4 bg-green-100 text-green-700 rounded-lg">
              Reset password email sent! Please check your inbox.
            </div>
          )}
        </div>

        <div className="text-center text-sm font-light text-[#9FA1A7] mt-[24px]">
          Back to{' '}
          <a href="/signin" className="text-[#222222] font-light hover:underline">
            Sign in
          </a>
        </div>
      </div>

      <div className="mt-auto w-full flex justify-center">
        <div className="text-center w-[253px] text-xs text-[#9FA1A7]">
          By using Clodexa, you are agreeing to our{' '}
          <a href="/privacy" className="text-[#222222] hover:underline">privacy policy</a>
          {' '}and{' '}
          <a href="/terms" className="text-[#222222] hover:underline">terms of service</a>
        </div>
      </div>
    </div>
  )

  return (
    <div className="min-h-screen h-screen flex flex-col items-center">
      <OnboardingCard 
        leftPanel={leftPanelContent}
        rightPanel={<AuthRightPanel />}
      />

      <ErrorPopup
        isOpen={isErrorPopupOpen}
        onClose={() => setIsErrorPopupOpen(false)}
        message={error || 'An error occurred'}
      />
    </div>
  )
}
